import { TableActionButton, TableActionGroup } from "@channelplay/table-actions";
// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/filter/report layout from the design system)
import ReportPageScaffold from '../components/ReportPageScaffold';
import DateRangeFilter from '../components/DateRangeFilter';
import PaginationFooter from '../components/PaginationFooter';
import { ReportLoadingState } from '../components/ReportLoadingState';
import { useState, useEffect, useMemo, useCallback, useId } from 'react';
import { Download, FileSpreadsheet, SlidersHorizontal, Store, User, Clock, MapPin, Users, X } from 'lucide-react';
import DashboardFilterModal, { type FilterSection } from '../components/dashboard/DashboardFilterModal';
import { storeVisitsReportAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { PAGE_SIZE_OPTIONS } from '../constants/reportPageSize';
import { buildCsv, downloadCsv } from '../utils/csv';
import { downloadTableXlsx, type TableSheet } from '../utils/tableXlsx';
import { usePagination } from '../hooks/usePagination';
import { useReportFilters } from '../hooks/useReportFilters';
import { useLinkedSearch } from '../hooks/useLinkedSearch';
import { useStoreRegions } from '../hooks/useStoreRegions';
import CustomReportPanel from '../components/CustomReportPanel';
import { useTableSort, SortTh } from '../components/dashboard/useTableSort';
import VisitsModal, {
  ManagerTabStrip, NotRecorded, SUGGESTED_RECORDED_FROM_LABEL, csvCell, formatDuration, formatTime, noneRecordedReason, safeDate,
  useDialogKeyboard,
  type ManagerTabs, type VisitRow, type VisitsModalMode,
} from '../components/store-visits/VisitsModal';

// One collapsed row: a store (Store view) or a manager (Manager view). Keeps the
// underlying sessions so the drill-down can list every individual visit + its activity.
interface AggRow {
  key: string;
  // Store view fields (blank in manager view) / Manager view fields (blank in store view).
  storeName: string;
  storeCode: string | null;
  city: string | null;
  state: string | null;
  category: string | null;
  managerName: string;
  managerCode: string;
  // Aggregates over the group's sessions.
  visits: number;
  otherCount: number;           // distinct managers (store view) or distinct stores (manager view)
  totalMinutes: number;
  // The phone's suggested times over the visits that have one (Manager View's Suggested
  // Time): their sum, how many visits have one, and the finished time spent on just those
  // visits plus how many of them are still open (for the hover).
  suggestedMinutes: number;
  suggestedVisits: number;
  spentOnSuggested: number;
  openSuggested: number;
  activityCount: number;        // distinct task submissions across the group's visits
  lastVisit: string;
  sessions: VisitRow[];
}

type ViewMode = VisitsModalMode;

/** Lowest→highest, so the Role filter can list manager roles above promoter roles. */
const CATEGORY_ORDER = ['L1', 'L2', 'L3', 'L4', 'L5'];

// A deployed store from the server, carrying just the attributes the filters act on.
// Lets the "Total Stores" tile + coverage recompute their denominator against the
// current store-level filters (city/state/category/trade/store), not the whole project.
interface DeployedStore {
  storeId: string;
  storeName: string;
  storeCode: string | null;
  city: string | null;
  state: string | null;
  category: string | null;
  trade: string | null;
}

const uniqSorted = (values: (string | undefined | null)[]): string[] =>
  Array.from(new Set(values.filter((v): v is string => !!v && v.trim() !== '')))
    .sort((a, b) => a.localeCompare(b));

// Sentinel for the "stores with no trade set" option, so those stores (which an exact-trade
// match would silently drop) are filterable instead of invisible.
const NO_TRADE = '__none__';
const tradeMatch = (trade: string | null | undefined, filter: string): boolean =>
  filter === NO_TRADE ? (!trade || !String(trade).trim()) : trade === filter;

const pad2 = (n: number): string => String(n).padStart(2, '0');
const toDateStr = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
// The current CALENDAR quarter as [start, end] in YYYY-MM-DD — Q1 Jan–Mar, Q2
// Apr–Jun, Q3 Jul–Sep, Q4 Oct–Dec. The end is capped at today so the picker never
// shows a future date (store visits are historical, so a future end adds nothing).
const currentQuarterRange = (): { start: string; end: string } => {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), q * 3, 1);
  const quarterEnd = new Date(now.getFullYear(), q * 3 + 3, 0);
  const end = quarterEnd < now ? quarterEnd : now;
  return { start: toDateStr(start), end: toDateStr(end) };
};


// ─── Pending stores modal ────────────────────────────────────────────────────
// The handled stores a manager has NOT visited inside the applied date range — opened by
// clicking a Pending Stores count in Manager View, or from the Pending stores tab of the
// manager's pop-up. Read-only: a list to work from, with its own filter box and a CSV of
// exactly what is on screen.
function PendingStoresModal({
  open, managerName, managerCode, handled, stores, rangeLabel, tabs, onClose,
}: {
  open: boolean;
  managerName: string;
  managerCode: string;
  /** The manager's handled-store total — the denominator the pending count came out of. */
  handled: number;
  stores: DeployedStore[];
  rangeLabel: string;
  tabs?: ManagerTabs;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  // A different manager's list starts unfiltered.
  useEffect(() => { setQuery(''); }, [managerCode]);
  // The keyboard manners of the Visits pop-up, so its two tabs behave alike.
  const { panelRef, titleRef } = useDialogKeyboard(open, onClose);
  const titleId = useId();
  if (!open) return null;

  const q = query.toLowerCase().trim();
  const shown = q
    ? stores.filter((s) => [s.storeName, s.storeCode, s.city, s.state, s.category, s.trade]
        .some((v) => (v || '').toLowerCase().includes(q)))
    : stores;

  const nameOf = (s: DeployedStore): string => s.storeName || `Store ${s.storeId}`;
  const download = () => {
    const headers = ['Store', 'Store Code', 'City', 'State', 'Store Category', 'Trade'];
    const data = shown.map((s) => [
      nameOf(s), s.storeCode || '-', s.city || '-', s.state || '-', s.category || '-', s.trade || '-',
    ].map(csvCell));
    downloadCsv(`pending-stores-${managerCode}-${rangeLabel.replace(/\s+/g, '')}.csv`, buildCsv(headers, data));
  };

  const cellCls = 'px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div ref={panelRef} className="relative z-10 flex max-h-[85vh] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-amber-600">
              <Store className="h-3.5 w-3.5" /> Pending store visits
            </div>
            <h2 id={titleId} ref={titleRef} tabIndex={-1} className="mt-0.5 truncate text-lg font-semibold text-gray-900 focus:outline-none">{managerName}</h2>
            <p className="truncate text-sm text-gray-500">
              {stores.length} of {handled} handled store{handled === 1 ? '' : 's'} not visited · {rangeLabel}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={download}
              disabled={shown.length === 0}
              className="flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
            <button type="button" onClick={onClose} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {tabs && <ManagerTabStrip tabs={tabs} />}

        {/* Body — the filter and the stores still to be visited, on the same grey canvas and
            white block as the Visits tab, so switching tabs keeps one look. */}
        <div className="flex min-h-0 flex-1 flex-col gap-3 rounded-b-2xl bg-gray-50 p-4 sm:p-5">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by store, code, city, state..."
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-400 focus:outline-none"
          />

          <div className="min-h-0 overflow-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  {['STORE', 'STORE CODE', 'CITY', 'STATE', 'CATEGORY', 'TRADE'].map((h, i) => (
                    <th key={h} className={`sticky top-0 z-10 whitespace-nowrap border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-[11px] font-semibold tracking-wide text-gray-500 ${i === 0 ? 'pl-5 text-left' : 'text-center'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {shown.map((s) => (
                  <tr key={s.storeId} className="transition-colors hover:bg-gray-50/70">
                    <td className={`${cellCls} pl-5 font-medium text-gray-900`}>{nameOf(s)}</td>
                    <td className={`${cellCls} text-center`}>{s.storeCode || '—'}</td>
                    <td className={`${cellCls} text-center`}>{s.city || '—'}</td>
                    <td className={`${cellCls} text-center`}>{s.state || '—'}</td>
                    <td className={`${cellCls} text-center`}>{s.category || '—'}</td>
                    <td className={`${cellCls} text-center`}>{s.trade || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {shown.length === 0 && (
              <p className="py-10 text-center text-sm text-gray-500">
                {stores.length === 0 ? 'No pending stores — every handled store was visited in this range.' : 'No stores match this filter.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StoreVisitsReportPage() {
  const { user, selectedProjectId } = useAuthStore();
  const [rows, setRows] = useState<VisitRow[]>([]);
  // Full in-scope manager roster (from the backend) — lets Manager View list managers who
  // have ZERO visits in range, not just those with a session/submission.
  const [managers, setManagers] = useState<{ code: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('store');
  // Role of each visiting employee, from the server. Drives the Role filter, which is the
  // only thing separating an L1 promoter's own-store check-ins from supervisory visits.
  const [visitorRoles, setVisitorRoles] = useState<Record<string, { roleName: string; category: string }>>({});
  const [roleFilter, setRoleFilter] = useState('all');
  const [drill, setDrill] = useState<{ mode: ViewMode; title: string; subtitle: string; fileKey: string; sessions: VisitRow[] } | null>(null);
  // Manager View "Pending Stores" drill-down: the handled stores this manager has NOT
  // visited inside the applied date range.
  const [pendingDrill, setPendingDrill] = useState<{ managerName: string; managerCode: string; handled: number; stores: DeployedStore[] } | null>(null);

  const {
    startDate, setStartDate,
    endDate, setEndDate,
    appliedStartDate, appliedEndDate,
    pageSize, setPageSize,
    currentPage, setCurrentPage,
    hasPendingDateChanges,
    applyDateRange,
  } = useReportFilters({ initialStartDate: currentQuarterRange().start, initialEndDate: currentQuarterRange().end });
  // The search text is kept here, not in the shared filter state, because a link can
  // open the report narrowed to one manager or one store (?employeeCode=, ?storeCode=)
  // and the box must hand that back to the address when the person changes it.
  const [searchQuery, setSearchQuery] = useLinkedSearch();

  const [storeFilter, setStoreFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [managerFilter, setManagerFilter] = useState('all');
  const [thFilter, setThFilter] = useState('all');
  const [tradeFilter, setTradeFilter] = useState('all');
  // Region — a COARSER cut than State, resolved from each store's state via the
  // 1Office master (we store no region of our own). Same resolution as the Dashboard.
  const [regionFilter, setRegionFilter] = useState('all');
  const { regionOf, regionsFrom } = useStoreRegions();
  // Stores with someone deployed (server-computed) — the denominator for visit coverage.
  const [totalDeployedStores, setTotalDeployedStores] = useState(0);
  // The deployed stores + their attributes, so Total Stores / coverage can recompute the
  // denominator against the current store-level filters (not just the whole-project count).
  const [deployedStores, setDeployedStores] = useState<DeployedStore[]>([]);
  // Per-manager count of stores each manager currently handles (server-computed), keyed by
  // manager code — the denominator the Manager View pairs with visited stores ("X of Y").
  // The store IDs behind each manager's handled count — the client derives the (optionally
  // store-level-filtered) count via handledCountFor, so no separate count map is needed.
  const [handledStoreIdsByManager, setHandledStoreIdsByManager] = useState<Record<string, string[]>>({});
  // Attributes for handled stores missing from `deployedStores` (a store whose deployed staff
  // have all left is handled but not "deployed"). Kept apart from deployedStores so the
  // coverage DENOMINATOR is unchanged — this only names/filters stores in the pending list.
  const [handledStoreDetails, setHandledStoreDetails] = useState<DeployedStore[]>([]);
  // Role cohorts (Assistant Manager / Trade Head), server-resolved by role name. Empty on a
  // project whose roles are named differently — callers below fall back to the old
  // row-derived behaviour in that case.
  const [amManagers, setAmManagers] = useState<{ code: string; name: string }[]>([]);
  const [thManagers, setThManagers] = useState<{ code: string; name: string }[]>([]);

  const storeOptions = useMemo(() => uniqSorted(rows.map(r => r.storeName)), [rows]);
  const cityOptions = useMemo(() => uniqSorted(rows.map(r => r.city)), [rows]);
  const stateOptions = useMemo(() => uniqSorted(rows.map(r => r.state)), [rows]);
  const regionOptions = useMemo(() => regionsFrom(rows.map(r => r.state)), [rows, regionsFrom]);
  const categoryOptions = useMemo(() => uniqSorted(rows.map(r => r.category)), [rows]);
  // Trade options span both the deployed-store roster and the visited rows, so a trade with
  // deployed stores but no visits in range is still selectable.
  const tradeOptions = useMemo(
    () => uniqSorted([...deployedStores.map(d => d.trade), ...rows.map(r => r.trade)]),
    [deployedStores, rows],
  );
  // Keyed on CODE, not name. The old name-keyed version collapsed two managers who share a
  // display name into one option, and split anyone whose name failed to resolve (the row
  // falls back to the bare code) into a second, unselectable one.
  const codeNamePairs = (
    getCode: (r: VisitRow) => string | null | undefined,
    getName: (r: VisitRow) => string | null | undefined,
  ) => {
    const m = new Map<string, string>();
    rows.forEach((r) => {
      const c = (getCode(r) || '').trim();
      if (c && !m.has(c)) m.set(c, getName(r) || c);
    });
    return [...m.entries()]
      .map(([value, name]) => ({ value, label: `${name} (${value})` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };
  // The AM / Trade Head pickers list ROLE HOLDERS, from the server's role cohorts — not
  // whoever happens to appear in `rows`. Rows are positional: `managerCode` is simply who
  // checked into a store and `thCode` is that person's one-hop manager, so the old
  // row-derived options listed every visiting employee under a label promising a role.
  //
  // Fall back to the row-derived lists when a cohort is empty, which is how a project that
  // names its roles something other than AM / Trade Heads keeps working instead of getting
  // two empty dropdowns.
  const cohortOptions = (cohort: { code: string; name: string }[]) =>
    cohort.map((m) => ({ value: m.code, label: `${m.name} (${m.code})` }));
  /**
   * Roles actually present in this report (visitors + the zero-visit roster), so picking one
   * can never blank the page by accident. Highest category first — manager roles above
   * promoter roles. The category rides in the label so "which level am I looking at" is
   * answerable without a second control.
   */
  const roleOptions = useMemo(() => {
    const seen = new Map<string, { value: string; label: string; rank: number }>();
    const add = (code?: string | null) => {
      const role = code ? visitorRoles[code] : undefined;
      if (!role?.roleName || seen.has(role.roleName)) return;
      seen.set(role.roleName, {
        value: role.roleName,
        label: `${role.roleName} · ${role.category}`,
        rank: CATEGORY_ORDER.indexOf(role.category),
      });
    };
    rows.forEach((r) => add(r.managerCode));
    managers.forEach((m) => add(m.code));
    return [...seen.values()].sort((a, b) => b.rank - a.rank || a.label.localeCompare(b.label));
  }, [rows, managers, visitorRoles]);

  const roleOf = useCallback(
    (code?: string | null) => (code ? visitorRoles[code]?.roleName ?? '' : ''),
    [visitorRoles],
  );

  const rowManagerOptions = useMemo(() => codeNamePairs((r) => r.managerCode, (r) => r.managerName), [rows]);
  const rowThOptions = useMemo(() => codeNamePairs((r) => r.thCode, (r) => r.thName), [rows]);
  const managerOptions = useMemo(
    () => (amManagers.length > 0 ? cohortOptions(amManagers) : rowManagerOptions),
    [amManagers, rowManagerOptions],
  );
  const thOptions = useMemo(
    () => (thManagers.length > 0 ? cohortOptions(thManagers) : rowThOptions),
    [thManagers, rowThOptions],
  );

  useEffect(() => {
    if (storeFilter !== 'all' && !storeOptions.includes(storeFilter)) setStoreFilter('all');
    if (cityFilter !== 'all' && !cityOptions.includes(cityFilter)) setCityFilter('all');
    if (stateFilter !== 'all' && !stateOptions.includes(stateFilter)) setStateFilter('all');
    if (categoryFilter !== 'all' && !categoryOptions.includes(categoryFilter)) setCategoryFilter('all');
    if (managerFilter !== 'all' && !managerOptions.some(o => o.value === managerFilter)) setManagerFilter('all');
    if (thFilter !== 'all' && !thOptions.some(o => o.value === thFilter)) setThFilter('all');
    if (roleFilter !== 'all' && !roleOptions.some(o => o.value === roleFilter)) setRoleFilter('all');
    if (tradeFilter !== 'all' && tradeFilter !== NO_TRADE && !tradeOptions.includes(tradeFilter)) setTradeFilter('all');
    if (regionFilter !== 'all' && !regionOptions.includes(regionFilter)) setRegionFilter('all');
  }, [storeOptions, cityOptions, stateOptions, categoryOptions, managerOptions, thOptions, tradeOptions, regionOptions,
      storeFilter, cityFilter, stateFilter, categoryFilter, managerFilter, thFilter, tradeFilter, regionFilter]);

  const fetchReport = async () => {
    if (!selectedProjectId) { setRows([]); return; }
    setLoading(true);
    try {
      const response = await storeVisitsReportAPI.getReport({
        start_date: appliedStartDate,
        end_date: appliedEndDate,
        employeeCode: user?.userName,
        projectId: selectedProjectId ?? undefined,
      });
      setRows(response.data?.data?.rows || []);
      setManagers(response.data?.data?.managers || []);
      setAmManagers(response.data?.data?.amManagers || []);
      setVisitorRoles(response.data?.data?.visitorRoles || {});
      setThManagers(response.data?.data?.thManagers || []);
      setTotalDeployedStores(response.data?.data?.totalDeployedStores ?? 0);
      setDeployedStores(response.data?.data?.deployedStores || []);
      setHandledStoreIdsByManager(response.data?.data?.handledStoreIdsByManager || {});
      setHandledStoreDetails(response.data?.data?.handledStoreDetails || []);
    } catch (err) {
      console.error('Error fetching store visits report:', err);
      setRows([]);
      setManagers([]);
      setAmManagers([]);
      setThManagers([]);
      setTotalDeployedStores(0);
      setDeployedStores([]);
      setHandledStoreIdsByManager({});
      setHandledStoreDetails([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [appliedStartDate, appliedEndDate, selectedProjectId]);
  useEffect(() => { setCurrentPage(1); }, [searchQuery, storeFilter, cityFilter, stateFilter, regionFilter, categoryFilter, managerFilter, thFilter, tradeFilter, roleFilter, viewMode, pageSize]);
  // Close the drill-down when the underlying data or view changes.
  useEffect(() => { setDrill(null); setPendingDrill(null); }, [viewMode, appliedStartDate, appliedEndDate, selectedProjectId]);

  // A store passes the STORE-LEVEL filters (store / city / state / category / trade). Shared by
  // filteredRows, the deployed-store denominator, and the per-manager handled denominator, so a
  // trade selection (incl. "no trade") narrows all three the same way.
  const storeLevelMatch = useCallback((s: {
    storeName?: string | null; city: string | null; state: string | null; category: string | null; trade: string | null;
  } | undefined | null): boolean => {
    if (!s) return false;
    if (storeFilter !== 'all' && s.storeName !== storeFilter) return false;
    if (cityFilter !== 'all' && s.city !== cityFilter) return false;
    if (stateFilter !== 'all' && s.state !== stateFilter) return false;
    if (regionFilter !== 'all' && regionOf(s.state) !== regionFilter) return false;
    if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
    if (tradeFilter !== 'all' && !tradeMatch(s.trade, tradeFilter)) return false;
    return true;
  }, [storeFilter, cityFilter, stateFilter, regionFilter, regionOf, categoryFilter, tradeFilter]);

  // storeId → store attributes, for looking up a handled store. Spans the deployed roster
  // PLUS the handled-only stores the server fills in, so a handled store whose staff have
  // all left is still nameable and still testable against the store-level filters (before,
  // an unknown id silently failed every filter).
  const storeById = useMemo(
    () => new Map([...deployedStores, ...handledStoreDetails].map(d => [d.storeId, d])),
    [deployedStores, handledStoreDetails],
  );

  // Is any STORE-level filter active? Decides whether a handled store with no resolvable
  // attributes can be counted (it can't be tested, so a filter must drop it).
  const storeLevelFilterActive =
    storeFilter !== 'all' || cityFilter !== 'all' || stateFilter !== 'all' ||
    regionFilter !== 'all' || categoryFilter !== 'all' || tradeFilter !== 'all';

  // A manager's handled STORES, narrowed by the active store-level filters (so "Stores
  // Handled", coverage and the pending list all shrink to e.g. the WC stores they handle).
  // Returns the stores themselves — the pending drill-down needs to render them, and the
  // count is just their length.
  const handledStoresFor = useCallback((code: string): DeployedStore[] => {
    const ids = handledStoreIdsByManager[code] ?? [];
    const out: DeployedStore[] = [];
    for (const id of ids) {
      const store = storeById.get(id);
      if (store) {
        if (storeLevelMatch(store)) out.push(store);
      } else if (!storeLevelFilterActive) {
        // Unresolvable store: it IS handled, so it stays in the unfiltered total, but with no
        // attributes to test it has to drop out the moment a store-level filter is applied.
        out.push({ storeId: id, storeName: '', storeCode: null, city: null, state: null, category: null, trade: null });
      }
    }
    return out;
  }, [handledStoreIdsByManager, storeById, storeLevelMatch, storeLevelFilterActive]);

  const handledCountFor = useCallback(
    (code: string): number => handledStoresFor(code).length,
    [handledStoresFor],
  );

  // Sessions passing the dropdown + search filters — the shared basis for BOTH views.
  const filteredRows = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return rows.filter(r => {
      if (!storeLevelMatch(r)) return false;
      if (managerFilter !== 'all' && r.managerCode !== managerFilter) return false;
      if (thFilter !== 'all' && r.thCode !== thFilter) return false;
      if (roleFilter !== 'all' && roleOf(r.managerCode) !== roleFilter) return false;
      if (!q) return true;
      return (
        r.managerName?.toLowerCase().includes(q) ||
        r.managerCode?.toLowerCase().includes(q) ||
        r.storeName?.toLowerCase().includes(q) ||
        r.storeCode?.toLowerCase().includes(q) ||
        r.city?.toLowerCase().includes(q) ||
        r.state?.toLowerCase().includes(q) ||
        r.category?.toLowerCase().includes(q)
      );
    });
  }, [rows, searchQuery, storeLevelMatch, managerFilter, thFilter, roleFilter, roleOf]);

  // Deployed stores passing the STORE-LEVEL filters only — NOT the AM/TH/search filters, which
  // narrow the numerator, not the store universe. Denominator for the Total Stores tile / coverage.
  const filteredDeployedStores = useMemo(
    () => deployedStores.filter(storeLevelMatch),
    [deployedStores, storeLevelMatch],
  );
  // Falls back to the server's whole-project count if the deployed-store list didn't load.
  const deployedDenominator = deployedStores.length > 0 ? filteredDeployedStores.length : totalDeployedStores;
  // Do we actually KNOW the deployed universe? True whenever the list loaded OR the server sent a
  // count. Only false when the deployed-store data is wholly absent (API failure / old payload).
  // Lets the tiles tell a real filtered-zero ("0 staffed stores match") apart from "no data" (—).
  const coverageKnown = deployedStores.length > 0 || totalDeployedStores > 0;

  // Collapse the filtered sessions to one row per store OR per manager.
  const aggregate = (mode: ViewMode): AggRow[] => {
    const byKey = new Map<string, VisitRow[]>();
    for (const r of filteredRows) {
      const k = mode === 'store' ? r.storeId : r.managerCode;
      const arr = byKey.get(k);
      if (arr) arr.push(r); else byKey.set(k, [r]);
    }
    const out: AggRow[] = [];
    for (const [k, sessions] of byKey) {
      const others = new Set<string>();
      const subs = new Set<string>();
      let totalMinutes = 0;
      let suggestedMinutes = 0;
      let suggestedVisits = 0;
      let spentOnSuggested = 0;
      let openSuggested = 0;
      let lastVisit = '';
      for (const s of sessions) {
        others.add(mode === 'store' ? s.managerCode : s.storeId);
        for (const a of s.activities) subs.add(a.submissionId);
        if (s.durationMinutes != null) totalMinutes += s.durationMinutes;
        // A visit still open counts its suggestion (it is on record from sign-in) but has no
        // time spent yet, as in Total Time and the pop-up.
        if (s.suggestedMinutes != null) {
          suggestedMinutes += s.suggestedMinutes;
          suggestedVisits += 1;
          if (s.durationMinutes != null) spentOnSuggested += s.durationMinutes; else openSuggested += 1;
        }
        if (s.date > lastVisit) lastVisit = s.date;
      }
      const head = sessions[0];
      out.push({
        key: k,
        storeName: head.storeName,
        storeCode: head.storeCode,
        city: head.city,
        state: head.state,
        category: head.category,
        managerName: head.managerName,
        managerCode: head.managerCode,
        visits: sessions.length,
        otherCount: others.size,
        totalMinutes,
        suggestedMinutes,
        suggestedVisits,
        spentOnSuggested,
        openSuggested,
        activityCount: subs.size,
        lastVisit,
        sessions,
      });
    }
    // Most-active first, then alphabetical.
    out.sort((a, b) => (b.visits - a.visits)
      || (mode === 'store' ? a.storeName.localeCompare(b.storeName) : a.managerName.localeCompare(b.managerName)));
    return out;
  };

  const storeViewRows = useMemo(() => aggregate('store'), [filteredRows]);

  /**
   * Codes allowed in Manager View: the AM + Trade Head cohorts.
   *
   * null = don't filter, which covers the project whose roles are named something else
   * (both cohorts empty) — same fallback the pickers use, so no project regresses.
   */
  const roleScopedCodes = useMemo(() => {
    if (amManagers.length === 0 && thManagers.length === 0) return null;
    return new Set([...amManagers.map((m) => m.code), ...thManagers.map((m) => m.code)]);
  }, [amManagers, thManagers]);

  const managerViewRows = useMemo(() => {
    // An explicit Role pick OVERRIDES the AM/Trade-Head cohort: choosing a promoter role
    // would otherwise always come back empty, since promoters are never in that cohort.
    const scoped = roleFilter !== 'all' ? null : roleScopedCodes;
    const agg = aggregate('manager').filter((r) => !scoped || scoped.has(r.managerCode));
    // Zero-visit managers only make sense when the filters aren't narrowing to specific
    // stores (a store/city/state/category filter implies "managers who visited THAT store").
    // A Trade Head filter is employee-scoped: the roster carries no TH hop, so a zero-visit
    // manager can't be tested against it. Suppress the tail rather than list managers who
    // may sit under a different Trade Head entirely.
    const storeScoped = storeFilter !== 'all' || cityFilter !== 'all' || stateFilter !== 'all' || categoryFilter !== 'all' || thFilter !== 'all';
    if (storeScoped) return agg;
    const seen = new Set(agg.map((r) => r.managerCode));
    const q = searchQuery.toLowerCase().trim();
    const zeros: AggRow[] = managers
      .filter((m) => !seen.has(m.code))
      .filter((m) => !scoped || scoped.has(m.code))
      .filter((m) => roleFilter === 'all' || roleOf(m.code) === roleFilter)
      .filter((m) => managerFilter === 'all' || m.code === managerFilter)
      .filter((m) => !q || m.name.toLowerCase().includes(q) || m.code.toLowerCase().includes(q))
      .map((m) => ({
        key: m.code,
        storeName: '', storeCode: null, city: null, state: null, category: null,
        managerName: m.name, managerCode: m.code,
        visits: 0, otherCount: 0, totalMinutes: 0,
        suggestedMinutes: 0, suggestedVisits: 0, spentOnSuggested: 0, openSuggested: 0,
        activityCount: 0, lastVisit: '', sessions: [],
      }));
    // Visited managers first (by visit count), zero-visit managers alphabetical at the tail.
    return [...agg, ...zeros].sort((a, b) => (b.visits - a.visits) || a.managerName.localeCompare(b.managerName));
  }, [filteredRows, managers, roleScopedCodes, storeFilter, cityFilter, stateFilter, categoryFilter, managerFilter, thFilter, roleFilter, roleOf, searchQuery]);

  /**
   * Per-manager coverage of their OWN patch, keyed by manager code: how much of what they
   * handle they actually visited inside the applied date range, and which handled stores are
   * still pending.
   *
   * The numerator is the INTERSECTION (handled ∩ visited), not the STORES VISITED column.
   * Those two differ whenever a manager visits a store outside their own patch — covering for
   * a colleague, or a store since reassigned — and only the intersection keeps the row
   * internally consistent: covered + pending = handled, so the % and the Pending count can
   * never contradict each other (nor exceed 100%). `outside` is the rest of STORES VISITED
   * (covered + outside = visited). Both are shown as their own columns — HANDLED STORES
   * VISITED and VISITED OUTSIDE HANDLED LIST — because without them a row like "89 visited,
   * 77 handled, 88.3%, 9 pending" reads as a mistake.
   *
   * Computed once per render pass over the visible manager rows rather than inside the sort
   * accessors, which would re-walk every manager's handled-store list on each comparison.
   *
   * Caveat, inherited from the columns either side of it: a free-text SEARCH narrows the
   * visited sessions but not the handled roster, so coverage reads low while a search that
   * matches stores (a city, a store name) is active — exactly as STORES VISITED already does.
   */
  const managerCoverage = useMemo(() => {
    const out = new Map<string, { handled: number; covered: number; outside: number; pct: number | null; pending: DeployedStore[] }>();
    for (const r of managerViewRows) {
      const handled = handledStoresFor(r.managerCode);
      const visited = new Set(r.sessions.map((sess) => sess.storeId));
      const pending = handled
        .filter((store) => !visited.has(store.storeId))
        .sort((a, b) => a.storeName.localeCompare(b.storeName));
      const covered = handled.length - pending.length;
      out.set(r.managerCode, {
        handled: handled.length,
        covered,
        outside: visited.size - covered,
        // handled = 0 (a manager whose patch can't be resolved) → no denominator, so the
        // cell shows '—' rather than a fictitious 0%.
        pct: handled.length > 0 ? (covered / handled.length) * 100 : null,
        pending,
      });
    }
    return out;
  }, [managerViewRows, handledStoresFor]);

  const coverageOf = (code: string) => managerCoverage.get(code);

  // NOTE: Manager View lists AM / Trade Head role-holders only (see roleScopedCodes).
  // Visits by anyone else are still counted in Total Visits / Stores Visited / Store View,
  // so the Manager View rows do NOT sum to the tiles above them. On iPro that gap is 2
  // people and 18 visits. This is deliberate — a banner naming the difference was removed
  // on request — so treat "manager rows don't add up" as expected, not as a new bug.
  const isManagerView = viewMode === 'manager';

  // Column sorting — one hook per view, so each view keeps its own sort when you
  // toggle between them. Both row sets arrive pre-sorted by visits (desc), so that
  // is declared as the default: VISITS shows ▼ from the start, and the first click
  // on it visibly flips the order rather than re-applying the one already on
  // screen. Sorting runs on the whole filtered set BEFORE pagination.
  const storeSort = useTableSort<AggRow>(storeViewRows, {
    store: (r) => r.storeName,
    storeCode: (r) => r.storeCode,
    city: (r) => r.city,
    visits: (r) => r.visits,
    managers: (r) => r.otherCount,
    totalTime: (r) => r.totalMinutes,
    activities: (r) => r.activityCount,
    lastVisit: (r) => r.lastVisit, // YYYY-MM-DD → sorts chronologically as text
  }, { defaultKey: 'visits', defaultDir: 'desc' });
  const managerSort = useTableSort<AggRow>(managerViewRows, {
    manager: (r) => r.managerName,
    managerCode: (r) => r.managerCode,
    visits: (r) => r.visits,
    storesVisited: (r) => r.otherCount,
    storesHandled: (r) => managerCoverage.get(r.managerCode)?.handled ?? 0,
    // Same null rule as `pending` below: with no handled patch there is nothing to split
    // the visited stores against, so these rows sink with the '—' they render.
    handledVisited: (r) => {
      const c = managerCoverage.get(r.managerCode);
      return c && c.handled > 0 ? c.covered : null;
    },
    outsideHandled: (r) => {
      const c = managerCoverage.get(r.managerCode);
      return c && c.handled > 0 ? c.outside : null;
    },
    coverage: (r) => managerCoverage.get(r.managerCode)?.pct ?? null,
    // null (not 0) when there is no handled patch to be pending against, so those rows sink
    // to the bottom with the '—' they render, instead of sorting as "nothing left to visit".
    pending: (r) => {
      const c = managerCoverage.get(r.managerCode);
      return c && c.handled > 0 ? c.pending.length : null;
    },
    totalTime: (r) => r.totalMinutes,
    // null when no visit has a suggested time ("Not recorded", or no visits), so those rows
    // sink rather than sorting as a 0-minute suggestion.
    suggestedTime: (r) => (r.suggestedVisits > 0 ? r.suggestedMinutes : null),
    activities: (r) => r.activityCount,
    lastVisit: (r) => r.lastVisit, // '' on zero-visit managers → sinks to the bottom either way
  }, { defaultKey: 'visits', defaultDir: 'desc' });
  const activeSort = isManagerView ? managerSort : storeSort;
  const activeData = activeSort.sorted;
  const { totalPages, safePage, paginatedData } = usePagination(activeData, currentPage, pageSize);
  // A new sort order starts from page 1. (Can't join the filter reset effect above —
  // the sort hooks are declared after it.)
  useEffect(() => { setCurrentPage(1); }, [activeSort.sortKey, activeSort.sortDir]);

  const summary = useMemo(() => {
    const stores = new Set<string>();
    const managerSet = new Set<string>();
    let totalMinutes = 0;
    for (const r of filteredRows) {
      stores.add(r.storeId);
      managerSet.add(r.managerCode);
      if (r.durationMinutes != null) totalMinutes += r.durationMinutes;
    }
    // Include zero-visit managers in the Managers count (unless narrowed to specific stores),
    // so the tile matches the roster-inclusive Manager View.
    const storeScoped = storeFilter !== 'all' || cityFilter !== 'all' || stateFilter !== 'all' || categoryFilter !== 'all' || thFilter !== 'all';
    if (!storeScoped) {
      const q = searchQuery.toLowerCase().trim();
      for (const m of managers) {
        if (managerFilter !== 'all' && m.code !== managerFilter) continue;
        if (q && !(m.name.toLowerCase().includes(q) || m.code.toLowerCase().includes(q))) continue;
        managerSet.add(m.code);
      }
    }

    // AM / Trade Head counts, by ROLE. Counted off the server's cohorts rather than off
    // `managerSet` (which is positional — "has a reportee") so the tiles answer "how many
    // AMs / Trade Heads are there", which is what the labels claim. On iPro the positional
    // number was 36 against 28 AMs + 5 Trade Heads.
    //
    // Filter behaviour deliberately MIRRORS the Managers tile above: a store-level filter
    // narrows to role-holders who actually visited the matching stores; otherwise every
    // role-holder counts, including those with no visits in the range.
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch = (m: { code: string; name: string }) =>
      !q || m.name.toLowerCase().includes(q) || m.code.toLowerCase().includes(q);
    const cohortCount = (
      cohort: { code: string; name: string }[],
      selected: string,
    ): number => cohort.filter((m) => {
      if (selected !== 'all' && m.code !== selected) return false;
      if (!matchesSearch(m)) return false;
      if (storeScoped && !managerSet.has(m.code)) return false;
      return true;
    }).length;

    // The denominator behind BOTH the "Total Stores" tile and coverage tracks the filter:
    //   • AM or Trade Head filter → that person's OWN patch: the stores they HANDLE, further
    //     NARROWED by any active store-level filter. So "AM + trade WC" makes it "the WC stores
    //     this manager handles", matching the (also trade-filtered) visited numerator.
    //   • store-level filters (or none) → the DEPLOYED universe under those filters.
    //   • Free-text search → deployed universe, coverage suppressed (arbitrary numerator).
    // `coverageMode` drives the tile subtitles ("handled" vs "deployed"). A manager whose
    // handled count can't be resolved (e.g. a TH whose direct reports are AMs → 0) falls back
    // to the deployed universe with coverage suppressed, rather than showing visited ÷ 0.
    const managerScope = managerFilter !== 'all' ? managerFilter : (thFilter !== 'all' ? thFilter : null);
    let coveragePct: number | null = null;
    let coverageDenominator = deployedDenominator;
    let coverageMode: 'handled' | 'deployed' = 'deployed';
    if (managerScope && !q) {
      const handled = handledCountFor(managerScope);
      if (handled > 0) {
        coverageMode = 'handled';
        coverageDenominator = handled;
        coveragePct = (stores.size / handled) * 100;
      } // else: keep deployed denominator, coverage stays null
    } else if (!q) {
      coveragePct = deployedDenominator > 0 ? (stores.size / deployedDenominator) * 100 : null;
    }

    return {
      visits: filteredRows.length,
      stores: stores.size,
      managers: managerSet.size,
      amCount: cohortCount(amManagers, managerFilter),
      thCount: cohortCount(thManagers, thFilter),
      coveragePct,
      coverageDenominator,
      coverageMode,
      totalMinutes,
    };
  }, [filteredRows, managers, amManagers, thManagers, deployedDenominator, handledCountFor, storeFilter, cityFilter, stateFilter, categoryFilter, managerFilter, thFilter, searchQuery]);

  const filterSections: FilterSection[] = [
    // Trade Head sits BEFORE Assistant Manager — parent then child, matching the hierarchy
    // and the order the Dashboard / Employee Score pages use.
    { kind: 'select', key: 'tradeHead', label: 'Trade Head', value: thFilter, onChange: setThFilter,
      options: [{ value: 'all', label: 'All trade heads' }, ...thOptions] },
    { kind: 'select', key: 'manager', label: 'Assistant Manager', value: managerFilter, onChange: setManagerFilter,
      options: [{ value: 'all', label: 'All assistant managers' }, ...managerOptions] },
    { kind: 'select', key: 'store', label: 'Store', value: storeFilter, onChange: setStoreFilter,
      options: [{ value: 'all', label: 'All stores' }, ...storeOptions.map(s => ({ value: s, label: s }))] },
    { kind: 'select', key: 'category', label: 'Store Category', value: categoryFilter, onChange: setCategoryFilter,
      options: [{ value: 'all', label: 'All categories' }, ...categoryOptions.map(c => ({ value: c, label: c }))] },
    { kind: 'select', key: 'trade', label: 'Store Trade', value: tradeFilter, onChange: setTradeFilter,
      options: [
        { value: 'all', label: 'All trades' },
        ...tradeOptions.map(t => ({ value: t, label: t })),
        // Stores with a blank trade would silently drop under any specific trade — this makes
        // them explicitly selectable so they can be found (and their trade set).
        { value: NO_TRADE, label: '— No trade set —' },
      ] },
    { kind: 'select', key: 'state', label: 'State', value: stateFilter, onChange: setStateFilter,
      options: [{ value: 'all', label: 'All states' }, ...stateOptions.map(s => ({ value: s, label: s }))] },
    // Coarser cut than State, from the 1Office master — sits between them, as on the Dashboard.
    { kind: 'select', key: 'region', label: 'Region', value: regionFilter, onChange: setRegionFilter,
      options: [{ value: 'all', label: 'All regions' }, ...regionOptions.map(r => ({ value: r, label: r }))] },
    { kind: 'select', key: 'city', label: 'City', value: cityFilter, onChange: setCityFilter,
      options: [{ value: 'all', label: 'All cities' }, ...cityOptions.map(c => ({ value: c, label: c }))] },
  ];
  const activeFilterCount = [managerFilter, thFilter, storeFilter, categoryFilter, tradeFilter, stateFilter, regionFilter, cityFilter].filter(v => v !== 'all').length;
  const clearAllFilters = () => { setManagerFilter('all'); setThFilter('all'); setStoreFilter('all'); setCategoryFilter('all'); setTradeFilter('all'); setStateFilter('all'); setRegionFilter('all'); setCityFilter('all'); };

  const handleExportCSV = () => {
    const range = `${appliedStartDate}-to-${appliedEndDate}`;
    // HIGH-LEVEL export: the aggregated rows exactly as shown in the table for the
    // ACTIVE view (one row per manager, or per store), respecting the current
    // filters AND the current column sort — not the raw per-visit detail. Total Time is the same "Xh Ym" the
    // table shows; Total Minutes rides alongside so the numbers still sum/pivot
    // offline. Cells are only trimmed here — buildCsv does the quoting (see csvCell).
    // Suggested Time follows the same pattern, its minutes left blank (not '-') when no visit
    // has one, as in the pop-up's download, plus how many visits the sum covers.
    if (isManagerView) {
      const headers = ['Manager', 'Manager Code', 'Visits', 'Stores Visited', 'Stores Handled', 'Handled Stores Visited', 'Visited Outside Handled List', 'Coverage %', 'Pending Stores', 'Total Time', 'Total Minutes',
        'Suggested Time', 'Suggested Minutes', 'Visits With Suggested Time', 'Activities', 'Last Visit'];
      const data = managerSort.sorted.map((r) => {
        const cov = managerCoverage.get(r.managerCode);
        return [
          r.managerName, r.managerCode, String(r.visits), String(r.otherCount),
          String(cov?.handled ?? 0),
          // Unresolvable patch → '-' in these four columns, matching the '—' on screen. A bare
          // 0 would read as "fully covered, nothing pending", which is the opposite of unknown.
          cov && cov.handled > 0 ? String(cov.covered) : '-',
          cov && cov.handled > 0 ? String(cov.outside) : '-',
          cov?.pct == null ? '-' : cov.pct.toFixed(1),
          cov && cov.handled > 0 ? String(cov.pending.length) : '-',
          formatDuration(r.totalMinutes), String(r.totalMinutes),
          r.visits === 0 ? '-' : (r.suggestedVisits === 0 ? 'Not recorded' : formatDuration(r.suggestedMinutes)),
          r.suggestedVisits === 0 ? '' : String(r.suggestedMinutes),
          String(r.suggestedVisits),
          String(r.activityCount),
          r.lastVisit ? safeDate(r.lastVisit) : '-',
        ].map(csvCell);
      });
      downloadCsv(`store-visits-by-manager-${range}.csv`, buildCsv(headers, data));
    } else {
      const headers = ['Store', 'Store Code', 'City', 'State', 'Store Category', 'Visits', 'Managers', 'Total Time', 'Total Minutes', 'Activities', 'Last Visit'];
      const data = storeSort.sorted.map((r) => [
        r.storeName, r.storeCode || '-', r.city || '-', r.state || '-', r.category || '-',
        String(r.visits), String(r.otherCount),
        formatDuration(r.totalMinutes), String(r.totalMinutes), String(r.activityCount),
        r.lastVisit ? safeDate(r.lastVisit) : '-',
      ].map(csvCell));
      downloadCsv(`store-visits-by-store-${range}.csv`, buildCsv(headers, data));
    }
  };

  // FULL LIST export: one Excel file, two sheets — every visit behind the page's numbers
  // (who, which store, when) and every pending store under the manager it is pending for.
  // Unlike Download above, which is the table on screen for the active view, this is the same
  // file from either view. Both sheets follow the applied date range and the page's filters,
  // and the Pending Stores sheet is the Manager View's own pending lists laid end to end, so
  // a manager's rows there always equal their Pending Stores count on screen.
  const fullListSheets = (): TableSheet[] => {
    const visits = [...filteredRows].sort((a, b) =>
      (b.date || '').localeCompare(a.date || '') || (b.checkInTime || '').localeCompare(a.checkInTime || ''));
    const visitSheet: TableSheet = {
      name: 'Visits',
      headers: ['Visit Date', 'Manager', 'Manager Code', 'Role', 'Reports To', 'Store', 'Store Code', 'City', 'State',
        'Store Category', 'Trade', 'Check In', 'Check Out', 'Time Spent', 'Minutes Spent', 'Suggested Minutes', 'Activities', 'Tasks Submitted'],
      rows: visits.map((v) => [
        v.date ? safeDate(v.date) : '', v.managerName, v.managerCode, roleOf(v.managerCode), v.thName || '',
        v.storeName, v.storeCode || '', v.city || '', v.state || '', v.category || '', v.trade || '',
        v.checkInTime ? formatTime(v.checkInTime) : 'No check-in (task visit)',
        v.checkOutTime ? formatTime(v.checkOutTime) : (v.checkInTime ? 'In progress' : ''),
        v.durationMinutes == null ? '' : formatDuration(v.durationMinutes),
        v.durationMinutes,
        // The time the phone suggested for the visit; an empty cell when none was recorded.
        v.suggestedMinutes ?? null,
        v.activities.length,
        v.activities.map((a) => a.taskTitle).filter(Boolean).join('; '),
      ]),
    };
    const pendingSheet: TableSheet = {
      name: 'Pending Stores',
      headers: ['Manager', 'Manager Code', 'Role', 'Store', 'Store Code', 'City', 'State', 'Store Category', 'Trade'],
      rows: [...managerViewRows]
        .sort((a, b) => a.managerName.localeCompare(b.managerName))
        .flatMap((r) => (managerCoverage.get(r.managerCode)?.pending ?? []).map((st) => [
          r.managerName, r.managerCode, roleOf(r.managerCode),
          st.storeName || `Store ${st.storeId}`, st.storeCode || '', st.city || '', st.state || '', st.category || '', st.trade || '',
        ])),
    };
    return [visitSheet, pendingSheet];
  };
  const handleExportFullList = () => {
    void downloadTableXlsx(`store-visits-full-list-${appliedStartDate}-to-${appliedEndDate}`, fullListSheets());
  };

  const th = 'px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap';
  const tdBase = 'px-4 py-4 whitespace-nowrap text-sm text-center';
  const td = `${tdBase} text-gray-900`;
  // The muted "nothing to show" cell. Its own list, not `${td} text-gray-400`: with both
  // greys on one cell Tailwind emits text-gray-900 later, so the dash stayed dark.
  const tdMuted = `${tdBase} text-gray-400`;
  // A header label forced onto two lines. The space before <br /> keeps the header's text
  // content (what screen readers and the tests read) as the plain one-line label.
  const twoLine = (first: string, second: string) => (
    <span className="leading-tight">{first} <br />{second}</span>
  );

  const openDrill = (r: AggRow) => setDrill(
    isManagerView
      ? { mode: 'manager', title: r.managerName, subtitle: `Code ${r.managerCode}`, fileKey: r.managerCode, sessions: r.sessions }
      : { mode: 'store', title: r.storeName, subtitle: [r.storeCode, r.city].filter(Boolean).join(' · '), fileKey: r.storeCode || '', sessions: r.sessions }
  );
  const openPending = (r: AggRow) => {
    const cov = coverageOf(r.managerCode);
    if (!cov || cov.handled === 0) return;
    setPendingDrill({ managerName: r.managerName, managerCode: r.managerCode, handled: cov.handled, stores: cov.pending });
  };

  // The manager whose pop-up is open (either tab), and the strip that switches between its
  // two lists. Read from the live rows, so both counts follow the page's filters.
  const openManagerCode = drill?.mode === 'manager' ? drill.fileKey : (pendingDrill?.managerCode ?? null);
  const openManagerRow = openManagerCode ? managerViewRows.find((r) => r.managerCode === openManagerCode) : undefined;
  const managerTabs = (active: 'visits' | 'pending'): ManagerTabs | undefined => {
    if (!openManagerRow) return undefined;
    const cov = coverageOf(openManagerRow.managerCode);
    return {
      active,
      visitCount: openManagerRow.sessions.length,
      pendingCount: cov && cov.handled > 0 ? cov.pending.length : null,
      onSelect: (tab) => {
        if (tab === active) return;
        if (tab === 'pending') { setDrill(null); openPending(openManagerRow); }
        else { setPendingDrill(null); openDrill(openManagerRow); }
      },
    };
  };

  const ToggleButton = ({ mode, icon: Icon, label }: { mode: ViewMode; icon: typeof Store; label: string }) => (
    <button
      type="button"
      onClick={() => setViewMode(mode)}
      className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition ${
        viewMode === mode ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  // Coverage of a manager's own patch — handled ∩ visited ÷ handled (see managerCoverage).
  // Banded green / amber / red so a row that needs attention is findable without sorting.
  const CoverageCell = ({ r }: { r: AggRow }) => {
    const cov = coverageOf(r.managerCode);
    if (!cov || cov.pct == null) {
      return <td className={tdMuted} title="No handled stores resolved for this manager">—</td>;
    }
    const { outside } = cov;
    const tone = cov.pct >= 80 ? 'bg-emerald-50 text-emerald-700'
      : cov.pct >= 50 ? 'bg-amber-50 text-amber-700'
      : 'bg-rose-50 text-rose-700';
    return (
      <td
        className={td}
        title={`${cov.covered} of ${cov.handled} handled stores visited in this range`
          + (outside > 0 ? ` · ${outside} visited store${outside === 1 ? '' : 's'} outside this manager's handled list` : '')}
      >
        <span className={`inline-flex min-w-[52px] items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>
          {cov.pct.toFixed(1)}%
        </span>
      </td>
    );
  };

  // STORES VISITED split in two: handled stores visited (the Coverage % numerator) and
  // visited stores outside the handled list (which Coverage % ignores).
  const VisitedSplitCell = ({ r, part }: { r: AggRow; part: 'covered' | 'outside' }) => {
    const cov = coverageOf(r.managerCode);
    if (!cov || cov.handled === 0) {
      return <td className={tdMuted} title="No handled stores resolved for this manager">—</td>;
    }
    return <td className={td}>{cov[part]}</td>;
  };

  // Handled stores with no visit in range. The count opens the list of exactly those stores.
  // Called as a function, as the Actions button below is, not drawn as <PendingCell />: a
  // component made afresh on every render is a new type each time, so React would replace the
  // button on every render and the pop-up could not hand focus back to it on closing.
  const pendingCell = (r: AggRow) => {
    const cov = coverageOf(r.managerCode);
    if (!cov || cov.handled === 0) {
      return <td className={tdMuted} title="No handled stores resolved for this manager">—</td>;
    }
    const n = cov.pending.length;
    if (n === 0) {
      return <td className={tdMuted} title="Every handled store was visited in this range">0</td>;
    }
    return (
      <td className={td}>
        <button
          type="button"
          onClick={() => openPending(r)}
          className="inline-flex min-w-[28px] items-center justify-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 transition hover:bg-amber-100 hover:underline"
          title={`Show the ${n} store${n === 1 ? '' : 's'} still to be visited`}
        >
          {n}
        </button>
      </td>
    );
  };

  // The phone's suggested times, added up over the visits that have one, read beside Total
  // Time. When only some visits have one (older visits, task visits, …) the count of those
  // goes underneath, and the time actually spent on them is on hover.
  const SuggestedTimeCell = ({ r }: { r: AggRow }) => {
    if (r.visits === 0) return <td className={tdMuted} title="No visits in this date range">—</td>;
    if (r.suggestedVisits === 0) {
      return <td className={tdBase}><NotRecorded why={noneRecordedReason(r.sessions, handledStoreIdsByManager)} /></td>;
    }
    const all = r.suggestedVisits === r.visits;
    const on = all
      ? (r.visits === 1 ? 'Suggested on the 1 visit' : `Suggested on all ${r.visits} visits`)
      : `Suggested on ${r.suggestedVisits} of ${r.visits} visits`;
    // An open visit has no time spent yet, so the hover says how many of these are still open.
    const spent = r.suggestedVisits === 1 && r.openSuggested === 1
      ? 'still in progress'
      : `time spent on ${r.suggestedVisits === 1 ? 'it' : 'those'}: ${formatDuration(r.spentOnSuggested)}`
        + (r.openSuggested > 0 ? ` (${r.openSuggested} still in progress)` : '');
    return (
      <td className={`${td} cursor-help font-medium`} title={`${on} · ${spent}`}>
        {formatDuration(r.suggestedMinutes)}
        {!all && <div className="mt-0.5 text-[11px] font-normal text-gray-400">{r.suggestedVisits} of {r.visits} visits</div>}
      </td>
    );
  };

  const actionButton = (r: AggRow) => (
    <TableActionGroup><TableActionButton action="view" label={isManagerView ? 'View visits & pending stores' : 'View visits & activity'}
type="button"
onClick={() => openDrill(r)}
title={isManagerView ? 'View visits & pending stores' : 'View visits & activity'} /></TableActionGroup>
  );

  return (
    <CustomReportPanel
      sourceKey="store-visits"
      sourceLabel="Store Visits"
      promptPlaceholder='e.g. "Total time each manager spent on store visits this month, as a bar chart"'
    >
    <ReportPageScaffold
      title="Store Visits Report"
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      searchPlaceholder="Search by manager, store, code, city..."
      searchInputPaddingRight="pr-4"
      filters={
        <>
          <div className="inline-flex items-center rounded-md border border-gray-300 bg-white p-0.5 shadow-sm">
            <ToggleButton mode="store" icon={Store} label="Store View" />
            <ToggleButton mode="manager" icon={User} label="Manager View" />
          </div>

          {roleOptions.length > 0 && (
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              title="Scope every number on this page to one role"
              className={`rounded-md border px-3 py-2 text-sm font-medium shadow-sm transition ${
                roleFilter !== 'all'
                  ? 'border-primary-200 bg-primary-50 text-primary-700'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <option value="all">All roles</option>
              {roleOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}

          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className={`flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium shadow-sm transition ${
              activeFilterCount > 0 ? 'border-primary-200 bg-primary-50 text-primary-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
            title="Open filters"
          >
            <SlidersHorizontal className="w-4 h-4" /> Filters
            {activeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs font-bold text-white">{activeFilterCount}</span>
            )}
          </button>

          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            hasPendingDateChanges={hasPendingDateChanges}
            onStartDateChange={(e) => setStartDate(e.target.value)}
            onEndDateChange={(e) => setEndDate(e.target.value)}
            onStartDateKeyDown={(e) => { if (e.key === 'Enter' && hasPendingDateChanges) applyDateRange(); }}
            onEndDateKeyDown={(e) => { if (e.key === 'Enter' && hasPendingDateChanges) applyDateRange(); }}
            onApply={applyDateRange}
          />

          <button
            onClick={handleExportCSV}
            disabled={activeData.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Download
          </button>

          <button
            type="button"
            onClick={handleExportFullList}
            disabled={loading || (filteredRows.length === 0 && managerViewRows.length === 0)}
            title="One Excel file with two sheets: every visit (who, which store, when) and every pending store by manager"
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Full list (Excel)
          </button>
        </>
      }
    >
      {/* Seven tiles on one row from xl up. Below that they wrap (4 cols on sm, 2 on mobile)
          — seven across a narrow viewport would squeeze each tile under ~120px and wrap
          every label onto three lines. */}
      {!loading && filteredRows.length > 0 && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3">
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Store className="w-3.5 h-3.5" /> {summary.coverageMode === 'handled' ? 'Stores Handled' : 'Total Stores'}
            </div>
            {/* A real filtered-zero shows "0" (e.g. "No trade set" → 0 staffed no-trade stores),
                NOT "—" — the dash is reserved for a genuinely unknown deployed universe. */}
            <div className="text-lg font-semibold text-gray-900 mt-1">
              {summary.coverageDenominator > 0 ? summary.coverageDenominator : (coverageKnown ? 0 : '—')}
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5">
              {summary.coverageMode === 'handled'
                ? 'Handled by this manager'
                : (coverageKnown && summary.coverageDenominator === 0 ? 'No staffed stores match this filter' : 'With employees deployed')}
            </div>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500"><MapPin className="w-3.5 h-3.5" /> Total Visits</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">{summary.visits}</div>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500"><Store className="w-3.5 h-3.5" /> Stores Visited</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">{summary.stores}</div>
          </div>
          {/* Coverage = visited ÷ (deployed OR handled) stores, both narrowed by the active
              store-level filters. Suppressed to '—' when the denominator is 0 (nothing staffed
              matches — e.g. "No trade set") or under free-text search (arbitrary numerator). */}
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500"><Store className="w-3.5 h-3.5" /> Store Coverage</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">
              {summary.coveragePct == null ? '—' : `${summary.coveragePct.toFixed(1)}%`}
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5">
              {summary.coveragePct == null
                ? (coverageKnown && summary.coverageDenominator === 0 ? 'No staffed stores match this filter' : 'Not available for this filter')
                : `${summary.stores} of ${summary.coverageDenominator} ${summary.coverageMode === 'handled' ? 'handled' : 'deployed'} stores visited`}
            </div>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500"><Users className="w-3.5 h-3.5" /> AMs</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">{summary.amCount}</div>
            <div className="text-[11px] text-gray-400 mt-0.5">Assistant Manager role</div>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500"><Users className="w-3.5 h-3.5" /> Trade Heads</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">{summary.thCount}</div>
            <div className="text-[11px] text-gray-400 mt-0.5">Trade Head role</div>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500"><Clock className="w-3.5 h-3.5" /> Total Time</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">{formatDuration(summary.totalMinutes)}</div>
          </div>
        </div>
      )}

      {loading && <ReportLoadingState />}

      {!loading && filteredRows.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <p className="text-sm text-gray-500">No store visits found for this period</p>
        </div>
      )}

      {!loading && filteredRows.length > 0 && (
        <div className="overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/70">
                {isManagerView ? (
                  <tr>
                    <SortTh label="MANAGER" sortKey="manager" sort={managerSort} className={`${th} !text-left`} />
                    {/* Multi-word labels stack on two lines (twoLine). With the HANDLED STORES
                        VISITED / VISITED OUTSIDE HANDLED LIST split and SUGGESTED TIME this view
                        has 14 columns, and one-line labels put ~200px of it off-screen on a
                        1500px-wide window. */}
                    <SortTh label={twoLine('MANAGER', 'CODE')} sortKey="managerCode" sort={managerSort} align="center" className={th} />
                    <SortTh label="VISITS" sortKey="visits" sort={managerSort} align="center" className={th} />
                    <SortTh label={twoLine('STORES', 'VISITED')} sortKey="storesVisited" sort={managerSort} align="center" className={th} />
                    <SortTh label={twoLine('STORES', 'HANDLED')} sortKey="storesHandled" sort={managerSort} align="center" className={th} />
                    <SortTh label={twoLine('HANDLED STORES', 'VISITED')} sortKey="handledVisited" sort={managerSort} align="center" className={th}
                      title="Stores from this manager's handled list visited in this date range. Coverage % = this ÷ Stores Handled; Pending Stores = Stores Handled − this" />
                    <SortTh label={twoLine('VISITED OUTSIDE', 'HANDLED LIST')} sortKey="outsideHandled" sort={managerSort} align="center" className={th}
                      title="Stores visited in this date range that are not in this manager's handled list, e.g. covering for a colleague. Stores Visited = Handled Stores Visited + this. Not counted in Coverage %" />
                    <SortTh label="COVERAGE %" sortKey="coverage" sort={managerSort} align="center" className={th}
                      title="Unique handled stores visited in this date range, as a % of the stores this manager handles" />
                    <SortTh label={twoLine('PENDING', 'STORES')} sortKey="pending" sort={managerSort} align="center" className={th}
                      title="Handled stores with no visit in this date range — click a count to list them" />
                    <SortTh label={twoLine('TOTAL', 'TIME')} sortKey="totalTime" sort={managerSort} align="center" className={th} />
                    <SortTh label={twoLine('SUGGESTED', 'TIME')} sortKey="suggestedTime" sort={managerSort} align="center" className={th}
                      title={`What the phone's visit agenda suggested, added up over the visits that have one. Suggested times are saved from ${SUGGESTED_RECORDED_FROM_LABEL}, so older visits have none`} />
                    <SortTh label="ACTIVITIES" sortKey="activities" sort={managerSort} align="center" className={th} />
                    <SortTh label={twoLine('LAST', 'VISIT')} sortKey="lastVisit" sort={managerSort} align="center" className={th} />
                    <th className={`${th} !text-center`}>Actions</th>
                  </tr>
                ) : (
                  <tr>
                    <SortTh label="STORE" sortKey="store" sort={storeSort} className={`${th} !text-left`} />
                    <SortTh label="STORE CODE" sortKey="storeCode" sort={storeSort} align="center" className={th} />
                    <SortTh label="CITY" sortKey="city" sort={storeSort} align="center" className={th} />
                    <SortTh label="VISITS" sortKey="visits" sort={storeSort} align="center" className={th} />
                    <SortTh label="MANAGERS" sortKey="managers" sort={storeSort} align="center" className={th} />
                    <SortTh label="TOTAL TIME" sortKey="totalTime" sort={storeSort} align="center" className={th} />
                    <SortTh label="ACTIVITIES" sortKey="activities" sort={storeSort} align="center" className={th} />
                    <SortTh label="LAST VISIT" sortKey="lastVisit" sort={storeSort} align="center" className={th} />
                    <th className={`${th} !text-center`}>Actions</th>
                  </tr>
                )}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(paginatedData as AggRow[]).map((r) => (
                  <tr key={r.key} className="hover:bg-gray-50">
                    {isManagerView ? (
                      <>
                        <td className={`${td} !text-left font-medium`}>{r.managerName}</td>
                        <td className={td}>{r.managerCode}</td>
                        <td className={td}><span className="inline-flex min-w-[28px] items-center justify-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-semibold text-primary-700">{r.visits}</span></td>
                        <td className={td}>{r.otherCount}</td>
                        <td className={td}>{coverageOf(r.managerCode)?.handled ?? 0}</td>
                        <VisitedSplitCell r={r} part="covered" />
                        <VisitedSplitCell r={r} part="outside" />
                        <CoverageCell r={r} />
                        {pendingCell(r)}
                        <td className={`${td} font-medium`}>{formatDuration(r.totalMinutes)}</td>
                        <SuggestedTimeCell r={r} />
                        <td className={td}>{r.activityCount}</td>
                        <td className={td}>{safeDate(r.lastVisit)}</td>
                        <td className={td}>{actionButton(r)}</td>
                      </>
                    ) : (
                      <>
                        <td className={`${td} !text-left font-medium`}>{r.storeName}</td>
                        <td className={td}>{r.storeCode || '—'}</td>
                        <td className={td}>{r.city || '—'}</td>
                        <td className={td}><span className="inline-flex min-w-[28px] items-center justify-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-semibold text-primary-700">{r.visits}</span></td>
                        <td className={td}>{r.otherCount}</td>
                        <td className={`${td} font-medium`}>{formatDuration(r.totalMinutes)}</td>
                        <td className={td}>{r.activityCount}</td>
                        <td className={td}>{safeDate(r.lastVisit)}</td>
                        <td className={td}>{actionButton(r)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && activeData.length > 0 && (
        <PaginationFooter
          safePage={safePage}
          totalPages={totalPages}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          totalRows={activeData.length}
          onPageSizeChange={(value) => setPageSize(value)}
          onPrevPage={() => setCurrentPage((p) => Math.max(1, p - 1))}
          onNextPage={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        />
      )}

      <DashboardFilterModal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filters"
        sections={filterSections}
        activeCount={activeFilterCount}
        onClearAll={clearAllFilters}
      />

      <PendingStoresModal
        open={!!pendingDrill}
        managerName={pendingDrill?.managerName || ''}
        managerCode={pendingDrill?.managerCode || ''}
        handled={pendingDrill?.handled || 0}
        stores={pendingDrill?.stores || []}
        rangeLabel={`${safeDate(appliedStartDate)} – ${safeDate(appliedEndDate)}`}
        tabs={pendingDrill ? managerTabs('pending') : undefined}
        onClose={() => setPendingDrill(null)}
      />

      <VisitsModal
        open={!!drill}
        mode={drill?.mode || 'store'}
        title={drill?.title || ''}
        subtitle={drill?.subtitle || ''}
        sessions={drill?.sessions || []}
        rangeLabel={`${safeDate(appliedStartDate)} – ${safeDate(appliedEndDate)}`}
        fileKey={drill?.fileKey || ''}
        tabs={drill?.mode === 'manager' ? managerTabs('visits') : undefined}
        handledStoreIdsByManager={handledStoreIdsByManager}
        onClose={() => setDrill(null)}
      />
    </ReportPageScaffold>
    </CustomReportPanel>
  );
}
